'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { offlineDB, type OfflineBoothInfo } from '@/lib/offline-db';
import { useOfflineSync } from '@/hooks/use-offline-sync';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Download,
  CheckCircle2,
  Search,
  FileText,
  FileBarChart,
  ClipboardList,
  FileSpreadsheet,
  RefreshCw,
  Wifi,
  WifiOff,
  MapPin,
  Users,
  Shield,
  Building2,
  Clock,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export function OfficerDashboard() {
  const { token, user } = useAuthStore();
  const { currentView, setCurrentView, dataDownloaded, setDataDownloaded, isOnline, lastSyncTime, pendingSyncCount } = useAppStore();
  const { syncNow, pendingCount, syncEnabled, syncFrequencyMinutes } = useOfflineSync();
  const { toast } = useToast();

  const [boothInfo, setBoothInfo] = useState<OfflineBoothInfo | null>(null);
  const [verifiedCount, setVerifiedCount] = useState(0);
  const [totalVoters, setTotalVoters] = useState(0);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [localDataDownloaded, setLocalDataDownloaded] = useState(false);

  const boothId = user?.boothId || '';

  // Check if data is already downloaded on mount
  useEffect(() => {
    const checkData = async () => {
      try {
        await offlineDB.init();
        const voters = await offlineDB.getVoters(boothId);
        const hasData = voters.length > 0;

        if (hasData) {
          setLocalDataDownloaded(true);
          setDataDownloaded(true);
          setTotalVoters(voters.length);

          // Load booth info
          const info = await offlineDB.getBoothInfo(boothId);
          if (info) {
            setBoothInfo(info);
          }

          // Get verified count
          const vCount = await offlineDB.getVerificationCount(boothId);
          setVerifiedCount(vCount);
        }
      } catch (error) {
        console.error('Failed to check existing data:', error);
      }
    };

    if (boothId) {
      checkData();
    }
  }, [boothId, setDataDownloaded]);

  // Refresh counts when returning to dashboard
  useEffect(() => {
    const refreshCounts = async () => {
      if (!localDataDownloaded || !boothId) return;
      try {
        const vCount = await offlineDB.getVerificationCount(boothId);
        setVerifiedCount(vCount);
      } catch (error) {
        console.error('Failed to refresh counts:', error);
      }
    };

    if (currentView === 'officer-dashboard') {
      refreshCounts();
    }
  }, [currentView, localDataDownloaded, boothId]);

  const handleDownload = useCallback(async () => {
    if (!token || !boothId) return;

    setIsDownloading(true);
    setDownloadError(null);
    setDownloadProgress(10);

    try {
      const res = await fetch(`/api/officer/download?boothId=${boothId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'डाउनलोड विफल हुआ');
      }

      setDownloadProgress(40);
      const data = await res.json();

      if (!data.success) {
        throw new Error(data.error || 'डाउनलोड विफल हुआ');
      }

      setDownloadProgress(60);

      // Save voters to IndexedDB
      if (data.voters && data.voters.length > 0) {
        await offlineDB.saveVoters(data.voters);
      }

      setDownloadProgress(80);

      // Save booth info to IndexedDB
      if (data.booth) {
        const boothData: OfflineBoothInfo = {
          pollingBoothId: data.booth.pollingBoothId,
          name: data.booth.name,
          address: data.booth.address || '',
          district: data.booth.district || '',
          constituency: data.booth.constituency || '',
          constituencyNo: data.booth.constituencyNo || '',
          wardNo: data.booth.wardNo || '',
          totalVoters: data.booth.totalVoters || data.voters?.length || 0,
          downloadedAt: new Date().toISOString(),
        };
        await offlineDB.saveBoothInfo(boothData);
        setBoothInfo(boothData);
      }

      setDownloadProgress(100);

      // Update state
      setTotalVoters(data.voters?.length || 0);
      setVerifiedCount(0);
      setLocalDataDownloaded(true);
      setDataDownloaded(true);
    } catch (error) {
      console.error('Download error:', error);
      setDownloadError(error instanceof Error ? error.message : 'डाउनलोड विफल हुआ');
    } finally {
      setIsDownloading(false);
    }
  }, [token, boothId, setDataDownloaded]);

  const handleSyncNow = useCallback(async () => {
    setIsSyncing(true);
    try {
      const result = await syncNow();
      if (result.success && result.syncedCount > 0) {
        toast({
          title: 'सिंक सफल',
          description: `${result.syncedCount} रिकॉर्ड सिंक हो गए`,
        });
      } else if (result.syncedCount === 0 && result.failedCount === 0) {
        toast({
          title: 'सिंक',
          description: 'कोई लंबित रिकॉर्ड नहीं है',
        });
      }
      // Refresh verified count after sync
      const vCount = await offlineDB.getVerificationCount(boothId);
      setVerifiedCount(vCount);
    } catch (error) {
      console.error('Sync failed:', error);
      toast({
        title: 'सिंक विफल',
        description: 'कृपया पुनः प्रयास करें',
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  }, [syncNow, boothId, toast]);

  const remainingCount = totalVoters - verifiedCount;
  const verificationPercentage = totalVoters > 0 ? Math.round((verifiedCount / totalVoters) * 100) : 0;

  const formatLastSyncTime = (time: string | null) => {
    if (!time) return 'कभी नहीं';
    try {
      const date = new Date(time);
      return date.toLocaleString('hi-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return time;
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
      {/* Booth Info Card */}
      <Card className="border-emerald-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-600" />
            <CardTitle className="text-lg text-emerald-800">
              मतदान कक्ष जानकारी
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {boothInfo ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <InfoItem icon={<Shield className="w-4 h-4" />} label="बूथ ID" value={boothInfo.pollingBoothId} />
                <InfoItem icon={<MapPin className="w-4 h-4" />} label="जिला" value={boothInfo.district || '-'} />
                <InfoItem icon={<Building2 className="w-4 h-4" />} label="बूथ नाम" value={boothInfo.name} />
                <InfoItem icon={<MapPin className="w-4 h-4" />} label="निर्वाचन क्षेत्र" value={boothInfo.constituency || '-'} />
              </div>
              <div className="border-t border-emerald-100 pt-3 mt-3">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-800">कुल मतदाता: {totalVoters}</span>
                </div>
                {localDataDownloaded && totalVoters > 0 && (
                  <div className="space-y-2">
                    <Progress value={verificationPercentage} className="h-3" />
                    <div className="flex justify-between text-sm">
                      <span className="text-emerald-700 font-medium">
                        ✓ सत्यापित: {verifiedCount}
                      </span>
                      <span className="text-amber-700 font-medium">
                        ⏳ शेष: {remainingCount > 0 ? remainingCount : 0}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-2 text-gray-500 text-sm">
              बूथ जानकारी लोड हो रही है...
            </div>
          )}
        </CardContent>
      </Card>

      {/* Download Section */}
      {!localDataDownloaded && (
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-emerald-50 shadow-sm">
          <CardContent className="pt-6 pb-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                <Download className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">डेटा डाउनलोड करें</h3>
                <p className="text-sm text-gray-600 mt-1">
                  ऑफलाइन उपयोग के लिए मतदाता डेटा डाउनलोड करें
                </p>
              </div>

              {isDownloading ? (
                <div className="space-y-3">
                  <Progress value={downloadProgress} className="h-3" />
                  <div className="flex items-center justify-center gap-2 text-emerald-700">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm font-medium">
                      डाउनलोड हो रहा है... {downloadProgress}%
                    </span>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={handleDownload}
                  size="lg"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-lg px-8 py-6 min-h-[56px] w-full max-w-xs"
                >
                  <Download className="w-5 h-5 mr-2" />
                  डेटा डाउनलोड
                </Button>
              )}

              {downloadError && (
                <div className="flex items-center justify-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{downloadError}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Ready Status */}
      {localDataDownloaded && (
        <Card className="border-emerald-200 bg-emerald-50/50 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="font-bold text-emerald-800">डेटा तैयार है</p>
                <p className="text-sm text-emerald-600">
                  {totalVoters} मतदाताओं का डेटा ऑफलाइन उपलब्ध है
                </p>
              </div>
              <Badge variant="outline" className="ml-auto border-emerald-300 text-emerald-700 bg-white">
                {totalVoters} मतदाता
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {localDataDownloaded && (
        <Card className="border-emerald-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-emerald-800">कार्य चुनें</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <ActionButton
                icon={<Search className="w-6 h-6" />}
                label="मतदाता खोज"
                sublabel="Voter Search"
                onClick={() => setCurrentView('voter-search')}
              />
              <ActionButton
                icon={<FileText className="w-6 h-6" />}
                label="14-A रिपोर्ट"
                sublabel="Form 14-A"
                onClick={() => setCurrentView('report-14a')}
              />
              <ActionButton
                icon={<FileBarChart className="w-6 h-6" />}
                label="15-A रिपोर्ट"
                sublabel="Form 15-A"
                onClick={() => setCurrentView('report-15a')}
              />
              <ActionButton
                icon={<ClipboardList className="w-6 h-6" />}
                label="17-A रिपोर्ट"
                sublabel="Form 17-A"
                onClick={() => setCurrentView('report-17a')}
              />
              <ActionButton
                icon={<FileSpreadsheet className="w-6 h-6" />}
                label="18-A रिपोर्ट"
                sublabel="Form 18-A"
                onClick={() => setCurrentView('report-18a')}
              />
              <ActionButton
                icon={<RefreshCw className="w-6 h-6" />}
                label="सिंक स्थिति"
                sublabel="Sync Status"
                onClick={() => setCurrentView('sync-status')}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sync Status Section */}
      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <CardTitle className="text-sm text-gray-700">सिंक स्थिति</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className={
                  syncEnabled
                    ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                    : 'border-amber-300 text-amber-700 bg-amber-50'
                }
              >
                {syncEnabled ? 'स्वचालित' : 'रुका हुआ'}
              </Badge>
              <Badge
                variant="outline"
                className={
                  isOnline
                    ? 'border-green-300 text-green-700 bg-green-50'
                    : 'border-red-300 text-red-700 bg-red-50'
                }
              >
                {isOnline ? (
                  <Wifi className="w-3 h-3 mr-1" />
                ) : (
                  <WifiOff className="w-3 h-3 mr-1" />
                )}
                {isOnline ? 'ऑनलाइन' : 'ऑफलाइन'}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">अंतिम सिंक:</span>
              <span className="text-gray-700 font-medium">
                {formatLastSyncTime(lastSyncTime)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">लंबित सिंक:</span>
              <span className={`font-medium ${pendingCount > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {pendingCount} रिकॉर्ड
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">सिंक आवृत्ति:</span>
              <span className="text-gray-700 font-medium">
                {syncFrequencyMinutes >= 60
                  ? `${Math.floor(syncFrequencyMinutes / 60)} घंटा ${syncFrequencyMinutes % 60 > 0 ? `${syncFrequencyMinutes % 60} मिनट` : ''}`
                  : `${syncFrequencyMinutes} मिनट`
                }
              </span>
            </div>

            {!syncEnabled && (
              <div className="flex items-center gap-2 text-amber-600 text-xs mt-1 bg-amber-50 rounded-lg p-2">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                <span>स्वचालित सिंक अडमिन द्वारा अक्षम किया गया है। मैन्युअल सिंक अभी भी काम करेगा।</span>
              </div>
            )}

            {isOnline && (
              <Button
                onClick={handleSyncNow}
                disabled={isSyncing}
                variant="outline"
                className="w-full mt-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 min-h-[44px]"
              >
                {isSyncing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    सिंक हो रहा है...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    अभी सिंक करें
                    {pendingCount > 0 && ` (${pendingCount} लंबित)`}
                  </>
                )}
              </Button>
            )}

            {!isOnline && pendingCount > 0 && (
              <div className="flex items-center gap-2 text-amber-600 text-xs mt-2 bg-amber-50 rounded-lg p-2">
                <WifiOff className="w-3 h-3 flex-shrink-0" />
                <span>ऑनलाइन होने पर सिंक स्वचालित होगा</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Helper component for booth info items
function InfoItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        {icon}
        <span>{label}</span>
      </div>
      <p className="text-sm font-medium text-gray-800 pl-5 truncate" title={value}>
        {value}
      </p>
    </div>
  );
}

// Helper component for action buttons
function ActionButton({
  icon,
  label,
  sublabel,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-1.5 p-4 rounded-xl border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all duration-200 min-h-[100px] active:scale-95 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2"
    >
      {icon}
      <span className="font-bold text-sm">{label}</span>
      <span className="text-xs opacity-70">{sublabel}</span>
    </button>
  );
}
